/**
 * Os Favorecidos da aba Cadastros: a busca, os cartões e o perfil de cada um.
 *
 * Papel: reproduz a metade de baixo de `#tab-cadastros` e `renderFavorecidos`
 * (l. 479–492, 5642–5728): o título com a contagem, "+ Novo Favorecido", a
 * busca, e um cartão por favorecido com 👤, o nome, ⚙ e ✕, e embaixo os selos
 * da categoria e da forma padrão — ou a dica "Sem padrões".
 *
 * Como usar: os favorecidos chegam da API em ordem alfabética, com `emUso`. A
 * busca é sem caixa e por trecho do nome. O ✕ remove sem perguntar, como na
 * origem, e fica desabilitado a 35 % no favorecido em uso; o ⚙ abre o perfil.
 * "+ Novo Favorecido" pergunta o nome pelo `prompt` do navegador, recusa o que
 * já existe sem diferenciar maiúsculas, cadastra — com o padrão de mesmo nome,
 * quando há — e abre o perfil; fica desabilitado enquanto a casa do caixa não
 * chega, em vez de perguntar o nome e não gravar. Quem só tem `ver` não vê
 * "+ Novo Favorecido", o ⚙ nem o ✕.
 */
import { type Categoria, type Favorecido, ROTULOS_FORMA } from '@concierge/shared';
import { IconSettings } from '@tabler/icons-react';
import { useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';
import { useCriarFavorecido, useExcluirFavorecido } from '../hooks/usar-financas';
import { Botao } from './kit/botao';
import { CLASSE_DO_CAMPO_DE_FILTRO } from './kit/campo-de-filtro';
import { Selo } from './kit/selo';
import { ModalDoPerfil } from './modal-do-perfil';
import { CLASSE_DO_TITULO_DE_CADASTROS } from './plano-de-contas';

interface ListaDeFavorecidosProps {
  /** Os favorecidos; indefinido enquanto não chegam. */
  favorecidos: readonly Favorecido[] | undefined;
  falhou: boolean;
  onTentarDeNovo: () => void;
  podeEditar: boolean;
  /** A casa do caixa, para cadastrar; nula enquanto não chega. */
  residenciaId: string | null;
  /** O plano de contas, para o perfil. */
  plano: readonly Categoria[];
}

/**
 * O plural de "favorecido".
 *
 * @param quantos - Quantos são.
 * @returns "favorecido" ou "favorecidos".
 */
function palavraFavorecido(quantos: number): string {
  return quantos === 1 ? 'favorecido' : 'favorecidos';
}

/**
 * Os Favorecidos.
 *
 * @param favorecidos - Os favorecidos, ou indefinido enquanto não chegam.
 * @param falhou - Se a consulta falhou.
 * @param onTentarDeNovo - Pede de novo.
 * @param podeEditar - Mostra o que grava.
 * @param residenciaId - A casa do caixa.
 * @param plano - O plano de contas.
 * @returns O título, a busca, a grade e o perfil aberto.
 */
export function ListaDeFavorecidos({
  favorecidos,
  falhou,
  onTentarDeNovo,
  podeEditar,
  residenciaId,
  plano,
}: ListaDeFavorecidosProps) {
  const notificar = useNotificacao();
  const criar = useCriarFavorecido();
  const excluir = useExcluirFavorecido();
  const [busca, setBusca] = useState('');
  const [perfilAberto, setPerfilAberto] = useState<Favorecido | null>(null);

  const todos = favorecidos ?? [];
  const termo = busca.trim().toLowerCase();
  const mostrados =
    termo === ''
      ? todos
      : todos.filter((favorecido) => favorecido.nome.toLowerCase().includes(termo));

  /**
   * Avisa a falha de uma gravação em vermelho.
   *
   * @param erro - O que a mutação lançou.
   */
  function avisarFalha(erro: unknown) {
    notificar(erro instanceof Error ? erro.message : 'Não foi possível gravar.', false);
  }

  /** O "+ Novo Favorecido" (l. 5617–5630). */
  async function handleNovo() {
    const nome = (window.prompt('Nome do favorecido:', '') ?? '').trim();
    if (nome === '' || residenciaId === null) return;
    const chave = nome.toLocaleLowerCase('pt-BR');
    if (todos.some((favorecido) => favorecido.nome.toLocaleLowerCase('pt-BR') === chave)) {
      notificar('Já existe', false);
      return;
    }
    try {
      const criado = await criar.mutateAsync({ residenciaId, nome });
      notificar('Favorecido adicionado');
      setPerfilAberto(criado);
    } catch (erro) {
      avisarFalha(erro);
    }
  }

  /**
   * O ✕: remove sem perguntar (l. 5632–5640).
   *
   * @param favorecido - O favorecido do cartão.
   */
  async function handleRemover(favorecido: Favorecido) {
    try {
      await excluir.mutateAsync(favorecido.id);
      notificar('Favorecido removido');
    } catch (erro) {
      avisarFalha(erro);
    }
  }

  let contagem = '—';
  if (favorecidos !== undefined) {
    contagem =
      termo === ''
        ? `${String(todos.length)} ${palavraFavorecido(todos.length)}`
        : `${String(mostrados.length)} de ${String(todos.length)} ${palavraFavorecido(todos.length)}`;
  }

  return (
    <div className="mt-8 border-t-2 border-emb-borda pt-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
        <div>
          <div className={CLASSE_DO_TITULO_DE_CADASTROS}>Favorecidos</div>
          <div className="mt-0.5 text-[11px] text-texto-fraco">{contagem}</div>
        </div>
        {podeEditar && (
          <Botao
            tamanho="sm"
            disabled={residenciaId === null}
            onClick={() => void handleNovo()}
          >
            + Novo Favorecido
          </Botao>
        )}
      </div>
      <div className="mb-3">
        <input
          type="text"
          aria-label="Buscar favorecido"
          placeholder="Buscar favorecido..."
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          className={cn(CLASSE_DO_CAMPO_DE_FILTRO, 'max-w-[320px]')}
        />
      </div>

      {favorecidos === undefined ? (
        <div className="py-2.5 text-[12px] text-texto-fraco italic">
          {falhou ? (
            <>
              Não foi possível carregar.{' '}
              <Botao variante="fantasma" tamanho="sm" onClick={onTentarDeNovo}>
                Tentar de novo
              </Botao>
            </>
          ) : (
            'Carregando…'
          )}
        </div>
      ) : mostrados.length === 0 ? (
        <div className="py-2.5 text-[12px] text-texto-fraco italic">
          {termo === '' ? 'Nenhum favorecido cadastrado.' : 'Nenhum favorecido encontrado.'}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-2.5">
          {mostrados.map((favorecido) => {
            const temPerfil =
              favorecido.categoriaPadrao !== null || favorecido.formaPagamentoPadrao !== null;
            return (
              <div
                key={favorecido.id}
                className="flex flex-col overflow-hidden rounded-[8px] border border-emb-borda bg-superficie-2"
              >
                <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                  <span aria-hidden="true" className="shrink-0 text-[18px]">
                    👤
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">
                    {favorecido.nome}
                  </span>
                  {podeEditar && (
                    <>
                      <Botao
                        variante="fantasma"
                        tamanho="sm"
                        icone
                        title="Editar perfil padrão"
                        aria-label={`Editar perfil padrão de ${favorecido.nome}`}
                        onClick={() => setPerfilAberto(favorecido)}
                      >
                        <IconSettings size={13} aria-hidden="true" />
                      </Botao>
                      <Botao
                        variante="perigo"
                        tamanho="sm"
                        icone
                        title={
                          favorecido.emUso ? 'Em uso — não é possível remover' : 'Remover'
                        }
                        aria-label={`Remover ${favorecido.nome}`}
                        disabled={favorecido.emUso}
                        className={favorecido.emUso ? 'opacity-35' : undefined}
                        onClick={() => void handleRemover(favorecido)}
                      >
                        ✕
                      </Botao>
                    </>
                  )}
                </div>
                {temPerfil ? (
                  <div className="flex flex-wrap items-center gap-1.5 pt-[5px] pr-3.5 pb-2 pl-[42px]">
                    {favorecido.categoriaPadrao !== null && (
                      <Selo cor="ouro">
                        {favorecido.categoriaPadrao}
                        {favorecido.subcategoriaPadrao === null
                          ? ''
                          : ` › ${favorecido.subcategoriaPadrao}`}
                      </Selo>
                    )}
                    {favorecido.formaPagamentoPadrao !== null && (
                      <Selo cor="azul">{ROTULOS_FORMA[favorecido.formaPagamentoPadrao]}</Selo>
                    )}
                  </div>
                ) : (
                  <div className="pr-3.5 pb-2 pl-[42px] text-[11px] text-texto-fraco italic">
                    Sem padrões — clique ⚙ para configurar
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {perfilAberto !== null && (
        <ModalDoPerfil
          favorecido={perfilAberto}
          plano={plano}
          onFechar={() => setPerfilAberto(null)}
          onSalvo={() => undefined}
        />
      )}
    </div>
  );
}
